import { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Aliases from world-atlas names to our data keys; UK constituents aggregate into "United Kingdom".
const UK_PARTS = ["england", "scotland", "wales", "northern ireland"];

function buildLookup(countries) {
  const map = {};
  let uk = 0;
  for (const { c, n } of countries) {
    const key = c.toLowerCase();
    if (UK_PARTS.includes(key)) uk += n;
    else map[key] = n;
  }
  map["united kingdom"] = (map["united kingdom"] || 0) + uk;
  return map;
}

function resolve(name, lookup) {
  let k = (name || "").toLowerCase().replace(/^the\s+/, "");
  if (k === "united states of america") k = "united states";
  return lookup[k] || 0;
}

export function WorldMap({ countries }) {
  const lookup = buildLookup(countries);
  const max = Math.max(...Object.values(lookup), 1);
  const [tip, setTip] = useState(null);

  const fillFor = (value) => {
    if (!value) return "hsl(var(--clan-ink-hsl) / 0.08)";
    const t = Math.log(value + 1) / Math.log(max + 1); // 0..1
    const alpha = (0.18 + 0.82 * t).toFixed(2);
    return `hsl(var(--accent-hsl) / ${alpha})`;
  };

  return (
    <div className="relative" onMouseLeave={() => setTip(null)}>
      <ComposableMap
        data-testid="world-map"
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies
              .filter((geo) => geo.properties.name !== "Antarctica")
              .map((geo) => {
                const value = resolve(geo.properties.name, lookup);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillFor(value)}
                    stroke="hsl(var(--clan-line-hsl))"
                    strokeWidth={0.5}
                    onMouseEnter={(e) =>
                      setTip({ name: geo.properties.name, value, x: e.clientX, y: e.clientY })
                    }
                    onMouseMove={(e) => setTip((p) => (p ? { ...p, x: e.clientX, y: e.clientY } : p))}
                    style={{
                      default: { outline: "none", transition: "fill 300ms" },
                      hover: { outline: "none", fill: "hsl(var(--accent-hsl))", cursor: "pointer" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
          }
        </Geographies>
      </ComposableMap>

      {tip && (
        <div
          className="pointer-events-none fixed z-[70] -translate-x-1/2 -translate-y-full rounded-md border border-clan-ink/15 bg-clan-bg/95 px-3 py-1.5 shadow-sm backdrop-blur-md"
          style={{ left: tip.x, top: tip.y - 10 }}
        >
          <p className="whitespace-nowrap font-serif text-sm text-clan-ink">{tip.name}</p>
          <p className="font-mono text-[11px] text-clan-accent">
            {tip.value ? tip.value.toLocaleString("en-US") : "—"}
          </p>
        </div>
      )}
    </div>
  );
}
